/** ADVANCED_VIDEO_GENERATION_CARD — Keyframe Timeline Strip (Tab list).
 *
 * Pure STAGE SELECTOR for Tab body / label clicks (P0_V2 §11-A/§18):
 * selecting a tab NEVER creates nodes and NEVER focuses a source.
 * Image thumbnail / empty-thumb clicks are a SEPARATE action that opens
 * or creates the keyframe's Generate Image source (P0_V2 §11-B). */

import { CheckCircleFilled, PictureOutlined, PlusOutlined } from '@ant-design/icons'
import { Tooltip } from 'antd'
import { useTranslation } from 'react-i18next'
import { resolveCanvasImageHoverPreviewUrl, resolveCanvasImageThumbUrl } from '../canvasImageQuality'
import {
  VIDEO_KEYFRAME_END_HANDLE,
  VIDEO_KEYFRAME_START_HANDLE,
  videoKeyframeMiddleHandleId,
  type KeyframeAssignment,
} from '../dataflow'
import { keyframeMaterialForEntry, keyframeSourceType, type KeyframeTimelineEntry } from '../videoKeyframeTimeline'
import ImageThumbnailHoverPreview from './ImageThumbnailHoverPreview'
import VideoInputSectionHandle from './VideoInputSectionHandle'

const ROLE_LABEL_KEY: Record<KeyframeTimelineEntry['role'], string> = {
  start: 'generation.keyframeRoleStart',
  middle: 'generation.keyframeRoleMiddle',
  end: 'generation.keyframeRoleEnd',
}

interface Props {
  workspaceId: string
  timeline: KeyframeTimelineEntry[]
  selectedKeyframeId: string | null
  assignment: KeyframeAssignment
  disabled?: boolean
  /** Tab body / label — SELECT ONLY (P0_V2 §11-A/§18). */
  onSelectKeyframe: (entryId: string) => void
  /** Image thumb / empty thumb — OPEN or CREATE source (P0_V2 §11-B). */
  onOpenKeyframeImageSource: (entryId: string) => void
  onAddKeyframe: () => void
  /** REMOVE KEYFRAME slot (separate from remove image). Only when N>1. */
  onRemoveKeyframe: (entryId: string) => void
  /** P0_V3 — REMOVE KEYFRAME IMAGE only (edge unbind; slot stays). */
  onRemoveKeyframeImage?: (entryId: string) => void
  /** "导入关键帧" — opens the canonical reused Image Asset Picker. */
  onImportKeyframe?: () => void
  /** VistoeraTASK-00064 P10-P12 — "清空关键帧": removes EVERY keyframe at
   * once and returns to pure text-to-video (spec §16's existing 0-
   * keyframe state). Only rendered while `timeline.length > 0`. */
  onClearKeyframes?: () => void
  /** Mount keyframe image input handles on timeline chips (DOM-owned). */
  sectionHandles?: boolean
}

export default function KeyframeTimelineStrip({
  workspaceId,
  timeline,
  selectedKeyframeId,
  assignment,
  disabled,
  onSelectKeyframe,
  onOpenKeyframeImageSource,
  onAddKeyframe,
  onRemoveKeyframe,
  onRemoveKeyframeImage,
  onImportKeyframe,
  onClearKeyframes,
  sectionHandles,
}: Props) {
  const { t } = useTranslation('canvas')

  return (
    <div className="pcv-brief-section pcv-video-keyframe-timeline-section" data-testid="video-keyframe-timeline">
      <span className="pcv-brief-section-label pcv-video-keyframe-section-label-row">
        <span>
          {t('generation.keyframeTimelineSection')}
          <span className="pcv-brief-section-optional">
            {timeline.length > 0
              ? t('generation.keyframeCountHint', { count: timeline.length })
              : t('generation.keyframeCountZeroHint')}
          </span>
        </span>
        <span className="pcv-video-keyframe-header-actions">
          {onClearKeyframes && timeline.length > 0 ? (
            <button
              type="button"
              className="pcv-video-keyframe-clear-btn nodrag"
              data-testid="video-keyframe-clear-btn"
              disabled={disabled}
              onClick={onClearKeyframes}
            >
              {t('generation.keyframeClearButton')}
            </button>
          ) : null}
          {onImportKeyframe ? (
            <button
              type="button"
              className="pcv-video-keyframe-import-btn nodrag"
              data-testid="video-keyframe-import-btn"
              disabled={disabled}
              onClick={onImportKeyframe}
            >
              <PictureOutlined /> {t('generation.keyframeImportButton')}
            </button>
          ) : null}
        </span>
      </span>
      <div className="pcv-video-keyframe-strip nowheel nodrag" data-testid="video-keyframe-strip" role="presentation">
        {timeline.map((entry, index) => {
          const material = keyframeMaterialForEntry(assignment, entry)
          const materialArgs = material
            ? {
                entityId: material.entityId,
                generatedOutputId: material.generatedOutputId,
                nodeType: material.nodeType,
              }
            : null
          const thumb = materialArgs ? resolveCanvasImageThumbUrl(workspaceId, materialArgs) : null
          const previewUrl = materialArgs ? resolveCanvasImageHoverPreviewUrl(workspaceId, materialArgs) : null
          const ready = !!material?.ready
          const isSelected = entry.id === selectedKeyframeId
          const sourceType = keyframeSourceType(material)
          const keyframeHandleId =
            entry.role === 'start'
              ? VIDEO_KEYFRAME_START_HANDLE
              : entry.role === 'end'
                ? VIDEO_KEYFRAME_END_HANDLE
                : videoKeyframeMiddleHandleId(entry.id)
          return (
            <Tooltip
              key={entry.id}
              title={`${t(ROLE_LABEL_KEY[entry.role])} · ${entry.relativeTimeSec.toFixed(1)}s`}
              mouseEnterDelay={0.3}
            >
              <div
                className={`pcv-video-keyframe-chip-wrap pcv-video-input-section${isSelected ? ' is-selected' : ''}`}
                data-video-input-port={keyframeHandleId}
              >
                {sectionHandles ? (
                  <VideoInputSectionHandle
                    id={keyframeHandleId}
                    className="pcv-handle pcv-handle--keyframe-input"
                    testId={
                      entry.role === 'start'
                        ? 'video-keyframe-start-in-handle'
                        : entry.role === 'end'
                          ? 'video-keyframe-end-in-handle'
                          : undefined
                    }
                  />
                ) : null}
                <button
                  type="button"
                  role="tab"
                  aria-selected={isSelected}
                  className={`pcv-video-keyframe-chip${isSelected ? ' is-selected' : ''}${ready ? ' is-ready' : ' is-empty'}`}
                  data-testid={`video-keyframe-chip-${entry.role}`}
                  data-keyframe-chip-id={entry.id}
                  data-keyframe-source-type={sourceType}
                  data-keyframe-index={index}
                  onClick={() => onSelectKeyframe(entry.id)}
                >
                <span
                  className="pcv-video-keyframe-chip-thumb"
                  data-testid={`video-keyframe-chip-thumb-${entry.role}`}
                  onClick={(e) => {
                    e.stopPropagation()
                    onOpenKeyframeImageSource(entry.id)
                  }}
                >
                  {thumb ? (
                    <ImageThumbnailHoverPreview
                      thumbUrl={thumb}
                      previewUrl={previewUrl}
                      className="pcv-video-keyframe-chip-thumb-img"
                    />
                  ) : (
                    <PictureOutlined className="pcv-video-keyframe-chip-thumb-empty-icon" />
                  )}
                  {ready ? <CheckCircleFilled className="pcv-video-keyframe-chip-ready-icon" /> : null}
                  {ready && onRemoveKeyframeImage ? (
                    <span
                      role="button"
                      aria-label={t('generation.keyframeRemoveImage')}
                      className="pcv-video-keyframe-chip-thumb-remove-image"
                      data-testid={`video-keyframe-chip-remove-image-${entry.role}`}
                      onClick={(e) => {
                        e.stopPropagation()
                        onRemoveKeyframeImage(entry.id)
                      }}
                    >
                      ×
                    </span>
                  ) : null}
                </span>
                <span className="pcv-video-keyframe-chip-meta">
                  <span className="pcv-video-keyframe-chip-role">{t(ROLE_LABEL_KEY[entry.role])}</span>
                  <span className="pcv-video-keyframe-chip-time">{entry.relativeTimeSec.toFixed(1)}s</span>
                </span>
                {/* VistoeraTASK-00064 P14 — previously hidden while
                 * `timeline.length <= 1`, which made the LAST keyframe
                 * un-removable via this per-card "×" (only "清空关键帧"
                 * above could reach 0). Removing a single-entry timeline
                 * here now correctly lands on the SAME explicit 0-
                 * keyframe / pure text-to-video state (spec §14
                 * "DELETE_LAST_KEYFRAME_EXITS_MODE=YES" — no other
                 * behavior changes; `onRemoveKeyframe` already persists
                 * `keyframeTimeline: []` when the filtered array is
                 * empty). */}
                <span
                  role="button"
                  aria-label={t('generation.keyframeRemove')}
                  className="pcv-video-keyframe-chip-remove"
                  data-testid={`video-keyframe-chip-remove-${entry.role}`}
                  onClick={(e) => {
                    e.stopPropagation()
                    onRemoveKeyframe(entry.id)
                  }}
                >
                  ×
                </span>
              </button>
              </div>
            </Tooltip>
          )
        })}
        <button
          type="button"
          className="pcv-video-keyframe-add-btn"
          data-testid="video-keyframe-add-btn"
          disabled={disabled}
          onClick={onAddKeyframe}
        >
          <PlusOutlined /> {t('generation.keyframeAdd')}
        </button>
      </div>
    </div>
  )
}
